import { createSlice } from '@reduxjs/toolkit'
import {
  CustomPreset,
  DomainDefaultColumns,
  getEtcPresets,
  getGeneralPresets,
  MetricPresets,
  PresetOrders,
} from 'features/dashboard/table/shared/Presets'
import api from 'api/axios/foka'
import { createAxiosThunk } from 'utils/reduxUtil'
import { arrayEquals, isPresent } from 'utils'
import { DashboardTypes } from 'features/Codes'
import {
  getSavedColumns,
  persistColumns,
} from 'features/dashboard/utils/storage/columnStorage'

import { GeneralColumns } from '../../../Columns'
import deepFreeze from 'deep-freeze'

const defaultAppliedColumnPresets = deepFreeze({
  GENERAL: false,
  BASIC: false,
  ADDITIONAL: false,
  PIXEL_AND_SDK: false,
  TALK_CHANNEL: false,
  ETC: false,
})

const DOMAIN_TYPES = {
  [DashboardTypes.CAMPAIGN]: 'CAMPAIGN',
  [DashboardTypes.AD_GROUP]: 'AD_GROUP',
  [DashboardTypes.KEYWORD]: 'KEYWORD',
  [DashboardTypes.CREATIVE]: 'CREATIVE_LINK',
}

export const fetchUserPresets = createAxiosThunk(
  'dashboard/table/config/fetchUserPresets',
  domainType => api.dashboard.getUserColumnPresets(DOMAIN_TYPES[domainType])
)

export const deleteUserPresets = createAxiosThunk(
  'dashboard/table/config/deleteUserPresets',
  async ({ id, domainType }) => {
    await api.dashboard.deleteUserColumnPresets(id)
    return api.dashboard.getUserColumnPresets(DOMAIN_TYPES[domainType])
  }
)

export const saveUserPreset = createAxiosThunk(
  'dashboard/table/config/saveUserPreset',
  async ({ name, domainType, columns }) => {
    const {
      data: { result: selectedPreset },
    } = await api.dashboard.saveUserColumnPresets({
      name,
      domainType: DOMAIN_TYPES[domainType],
      columns: columns.map(column => column.name),
    })
    const {
      data: { result: userPresets },
    } = await api.dashboard.getUserColumnPresets(DOMAIN_TYPES[domainType])
    return api.response({
      userPresets,
      selectedPreset,
    })
  }
)

export const updateUserPreset = createAxiosThunk(
  'dashboard/table/config/updateUserPreset',
  async ({ id, name, domainType, columns }) => {
    const {
      data: { result: selectedPreset },
    } = await api.dashboard.updateUserColumnPresets({
      id,
      name,
      domainType: DOMAIN_TYPES[domainType],
      columns: columns.map(column => column.name),
    })
    const {
      data: { result: userPresets },
    } = await api.dashboard.getUserColumnPresets(DOMAIN_TYPES[domainType])
    return api.response({
      userPresets,
      selectedPreset,
    })
  }
)

const getAppliedColumnPresets = (domainType, columns) => {
  if (columns === undefined) {
    return defaultAppliedColumnPresets
  } else {
    const defaultColumns = DomainDefaultColumns[domainType]
    const isDefault = arrayEquals(defaultColumns, columns)
    if (isDefault) {
      return defaultAppliedColumnPresets
    }
    const generalPresetColumns = getGeneralPresets(domainType).columns.map(
      ({ name }) => name
    )
    const basicPresetColumns = MetricPresets.BASIC.columns.map(
      ({ name }) => name
    )
    const additionalPresetColumns = MetricPresets.ADDITIONAL.columns.map(
      ({ name }) => name
    )
    const trackerPresetColumns = MetricPresets.PIXEL_AND_SDK.columns.map(
      ({ name }) => name
    )
    const talkChannelPresetColumns = MetricPresets.TALK_CHANNEL.columns.map(
      ({ name }) => name
    )
    const etcPresetColumns = getEtcPresets(domainType).columns.map(
      ({ name }) => name
    )

    const generalAppliedColumns = columns.filter(
      column => GeneralColumns[column]
    )
    const basicAppliedColumns = columns.filter(column =>
      basicPresetColumns.includes(column)
    )
    const additionalAppliedColumns = columns.filter(column =>
      additionalPresetColumns.includes(column)
    )
    const trackerAppliedColumns = columns.filter(column =>
      trackerPresetColumns.includes(column)
    )
    const talkChannelAppliedColumns = columns.filter(column =>
      talkChannelPresetColumns.includes(column)
    )
    const etcAppliedColumns = columns.filter(column =>
      etcPresetColumns.includes(column)
    )

    const generalChecked = arrayEquals(
      generalPresetColumns,
      generalAppliedColumns
    )
    const basicChecked = arrayEquals(basicPresetColumns, basicAppliedColumns)
    const additionalChecked = arrayEquals(
      additionalPresetColumns,
      additionalAppliedColumns
    )
    const trackerChecked = arrayEquals(
      trackerPresetColumns,
      trackerAppliedColumns
    )
    const talkChannelChecked = arrayEquals(
      talkChannelPresetColumns,
      talkChannelAppliedColumns
    )
    const etcChecked = arrayEquals(etcPresetColumns, etcAppliedColumns)

    const isCustom =
      !isDefault &&
      !arrayEquals(columns, [
        ...(generalChecked ? generalPresetColumns : []),
        ...(basicChecked ? basicPresetColumns : []),
        ...(additionalChecked ? additionalAppliedColumns : []),
        ...(trackerChecked ? trackerAppliedColumns : []),
        ...(talkChannelChecked ? talkChannelAppliedColumns : []),
        ...(etcChecked ? etcAppliedColumns : []),
      ])
    return isCustom
      ? {
          CUSTOM: true,
        }
      : {
          GENERAL: generalChecked,
          BASIC: basicChecked,
          ADDITIONAL: additionalChecked,
          PIXEL_AND_SDK: trackerChecked,
          TALK_CHANNEL: talkChannelChecked,
          ETC: etcChecked,
        }
  }
}
const getSavedPresets = () => {
  const presets = {
    [DashboardTypes.CAMPAIGN]: undefined,
    [DashboardTypes.AD_GROUP]: undefined,
    [DashboardTypes.KEYWORD]: undefined,
    [DashboardTypes.CREATIVE]: undefined,
  }
  const savedColumns = getSavedColumns()
  Object.keys(presets).forEach(key => {
    const { columns } = savedColumns[key]
    presets[key] = getAppliedColumnPresets(key, columns)
  })
  return presets
}

const getInitialState = () => {
  const savedColumns = getSavedColumns()
  const columns = Object.entries(savedColumns).reduce((acc, [key, value]) => {
    acc[key] = value.columns
    return acc
  }, {})
  const selectedUserPresetId = Object.entries(savedColumns).reduce(
    (acc, [key, value]) => {
      acc[key] = value.id || undefined
      return acc
    },
    {}
  )
  const appliedColumnPresets = getSavedPresets()
  return {
    columns,
    appliedColumnPresets,
    selectedUserPresetId,
    visible: {
      [DashboardTypes.CAMPAIGN]: false,
      [DashboardTypes.AD_GROUP]: false,
      [DashboardTypes.KEYWORD]: false,
      [DashboardTypes.CREATIVE]: false,
    },
    editing: {
      [DashboardTypes.CAMPAIGN]: undefined,
      [DashboardTypes.AD_GROUP]: undefined,
      [DashboardTypes.KEYWORD]: undefined,
      [DashboardTypes.CREATIVE]: undefined,
    },
    error: {
      [DashboardTypes.CAMPAIGN]: undefined,
      [DashboardTypes.AD_GROUP]: undefined,
      [DashboardTypes.KEYWORD]: undefined,
      [DashboardTypes.CREATIVE]: undefined,
    },
    loading: {
      [DashboardTypes.CAMPAIGN]: false,
      [DashboardTypes.AD_GROUP]: false,
      [DashboardTypes.KEYWORD]: false,
      [DashboardTypes.CREATIVE]: false,
    },
    isLoaded: {
      [DashboardTypes.CAMPAIGN]: false,
      [DashboardTypes.AD_GROUP]: false,
      [DashboardTypes.KEYWORD]: false,
      [DashboardTypes.CREATIVE]: false,
    },
    userPresets: {
      [DashboardTypes.CAMPAIGN]: [],
      [DashboardTypes.AD_GROUP]: [],
      [DashboardTypes.KEYWORD]: [],
      [DashboardTypes.CREATIVE]: [],
    },
  }
}

const getPresets = (key, domainType) => {
  if (key === 'GENERAL') {
    return getGeneralPresets(domainType)
  } else if (key === 'ETC') {
    return getEtcPresets(domainType)
  } else {
    return MetricPresets[key]
  }
}

const nullToEmptyArray = items =>
  items.map(item => ({
    ...item,
    ...(item.columns === null && { columns: [] }),
  }))

const tableConfigSlice = createSlice({
  name: 'dashboard/table/config',
  initialState: getInitialState(),

  reducers: {
    showPresetConfigModal: (state, { payload: { domainType, editing } }) => {
      if (editing) {
        state.editing[domainType] = editing
      }
      state.visible[domainType] = true
    },
    hidePresetConfigModal: (state, { payload: domainType }) => {
      state.editing[domainType] = undefined
      state.visible[domainType] = false
    },
    applyDefaultPreset: (state, { payload: domainType }) => {
      state.selectedUserPresetId[domainType] = undefined
      state.appliedColumnPresets[domainType] = defaultAppliedColumnPresets
      const columns = DomainDefaultColumns[domainType]
      state.columns[domainType] = columns
      persistColumns(domainType, columns)
    },
    applyPreset: (state, { payload: { preset, isChecked, domainType } }) => {
      state.selectedUserPresetId[domainType] = undefined
      state.appliedColumnPresets[domainType][CustomPreset.name] = false
      state.appliedColumnPresets[domainType][preset.name] = isChecked
      const columns = PresetOrders.map(key => {
        const checked = state.appliedColumnPresets[domainType][key]
        return checked ? getPresets(key, domainType) : undefined
      })
        .filter(Boolean)
        .flatMap(_preset => _preset.columns)
        .map(column => column.name)
      state.columns[domainType] = columns
      persistColumns(domainType, columns)
    },
    applyUserPreset: (state, { payload: { id, domainType } }) => {
      const selectedPreset = state.userPresets[domainType].find(
        ({ id: _id }) => _id === id
      )
      if (!isPresent(selectedPreset)) return
      Object.keys(state.appliedColumnPresets[domainType]).forEach(key => {
        state.appliedColumnPresets[domainType][key] = false
      })
      state.selectedUserPresetId[domainType] = id
      const { columns } = selectedPreset
      state.columns[domainType] = columns
      persistColumns(domainType, columns, id)
    },
    setColumns: (state, { payload: { columns, domainType } }) => {
      state.appliedColumnPresets[domainType] = getAppliedColumnPresets(
        domainType,
        columns
      )
      state.selectedUserPresetId[domainType] = undefined
      state.columns[domainType] = columns
      persistColumns(domainType, columns)
    },
    clearError: (state, { payload: domainType }) => {
      state.error[domainType] = undefined
    },
    reset: () => getInitialState(),
  },

  extraReducers: builder => {
    builder.addCase(
      fetchUserPresets.pending,
      (state, { meta: { arg: domainType } }) => {
        state.loading[domainType] = true
      }
    )

    builder.addCase(
      fetchUserPresets.fulfilled,
      (state, { payload: { result }, meta: { arg: domainType } }) => {
        const userPresets = nullToEmptyArray(result)
        const selectedUserPresetId = state.selectedUserPresetId[domainType]
        if (selectedUserPresetId) {
          state.appliedColumnPresets[domainType] = defaultAppliedColumnPresets
        }
        if (
          selectedUserPresetId &&
          userPresets.find(({ id }) => id === selectedUserPresetId) ===
            undefined
        ) {
          state.selectedUserPresetId[domainType] = undefined
          const columns = DomainDefaultColumns[domainType]
          state.columns[domainType] = columns
          persistColumns(domainType, columns)
        }
        state.userPresets[domainType] = userPresets
        state.loading[domainType] = false
        state.isLoaded[domainType] = true
      }
    )

    builder.addCase(
      fetchUserPresets.rejected,
      (state, { payload, meta: { arg: domainType } }) => {
        state.loading[domainType] = false
        state.isLoaded[domainType] = true
        state.error[domainType] = payload
      }
    )

    builder.addCase(
      deleteUserPresets.pending,
      (
        state,
        {
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = true
      }
    )

    builder.addCase(
      deleteUserPresets.fulfilled,
      (
        state,
        {
          payload: { result },
          meta: {
            arg: { domainType, id },
          },
        }
      ) => {
        if (id === state.selectedUserPresetId[domainType]) {
          state.selectedUserPresetId[domainType] = undefined
        }
        state.userPresets[domainType] = nullToEmptyArray(result)
        state.loading[domainType] = false
      }
    )

    builder.addCase(
      deleteUserPresets.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = false
        state.error[domainType] = payload
      }
    )

    builder.addCase(
      saveUserPreset.pending,
      (
        state,
        {
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = true
      }
    )

    builder.addCase(
      saveUserPreset.fulfilled,
      (
        state,
        {
          payload: { userPresets },
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = false
        state.userPresets[domainType] = nullToEmptyArray(userPresets)
      }
    )

    builder.addCase(
      saveUserPreset.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = false
        state.error[domainType] = payload
      }
    )

    builder.addCase(
      updateUserPreset.pending,
      (
        state,
        {
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = true
      }
    )

    builder.addCase(
      updateUserPreset.fulfilled,
      (
        state,
        {
          payload: { userPresets },
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = false
        state.userPresets[domainType] = nullToEmptyArray(userPresets)
      }
    )

    builder.addCase(
      updateUserPreset.rejected,
      (
        state,
        {
          payload,
          meta: {
            arg: { domainType },
          },
        }
      ) => {
        state.loading[domainType] = false
        state.error[domainType] = payload
      }
    )
  },
})

export default tableConfigSlice.reducer
export const {
  name,
  actions: {
    showPresetConfigModal,
    hidePresetConfigModal,
    applyDefaultPreset,
    applyPreset,
    applyUserPreset,
    setColumns,
    reset,
    clearError,
  },
} = tableConfigSlice
